/**
 * Module Pack Ailix - Logique de tarification multi-modes
 * 
 * Ce fichier contient la logique pour le switch 3 états de tarification :
 * - À la carte : TJM 700€, remises volume progressives selon config admin
 * - Pack Ailix 1 an : TJM équiv. 588€
 * - Pack Ailix 2+ ans : TJM équiv. 532€
 * 
 * PHASE TDD : Ce fichier est un STUB. Les fonctions doivent être implémentées
 * au Prompt 3 pour faire passer les tests du Prompt 1.
 * 
 * @see src/utils/__tests__/packAilixPricing.test.ts pour les tests
 * @see prompts/pack-ailix-context-state.md pour le contexte complet
 */

import { Mission } from '@/services/clientDataService';
import { calculateTotalDiscountPercentage } from '@/lib/formatUtils';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Modes de tarification disponibles
 */
export type PricingMode = 'a_la_carte' | 'pack_ailix_1an' | 'pack_ailix_2ans';

/**
 * Configuration d'une prestation individuelle dans le Pack Ailix
 */
export interface PackPrestationConfig {
  label: string;
  jours_par_mois: number;
  tjm_1an: number;
  tjm_2ans: number;
  /** Si true, jours_par_mois est multiplié par nbDevelopers */
  per_dev: boolean;
  enabled_by_default: boolean;
  /** TJM utilisé pour le calcul "À la carte" en mode hybride (optionnel, fallback clientTjm ou 1000) */
  tjm_a_la_carte?: number;
}

/**
 * Modèle de comparaison pour les surfaces Navigation/Finalisation/Modalité
 * - "legacy" : comportement actuel (À la carte = propsPricing one-shot)
 * - "hybrid" : dev au réel + prestations mensualisées pour À la carte aussi
 */
export type ComparisonModel = 'legacy' | 'hybrid';

/**
 * Configuration personnalisée du Pack Ailix pour un client
 */
export interface PackAilixConfig {
  tjm_pack_1an: number;
  tjm_pack_2ans: number;
  prestations: Record<string, PackPrestationConfig>;
  /** Modèle de comparaison (absent = "legacy" pour rétrocompatibilité) */
  comparison_model?: ComparisonModel;
}

/**
 * Résultat du calcul d'éligibilité Pack Ailix
 */
export interface PackAilixEligibility {
  /** true si totalDays >= 220 */
  eligible: boolean;
  /** Nombre de jours restants pour débloquer (0 si éligible) */
  remaining: number;
  /** Montant en € restant pour débloquer (remaining × 700€) */
  remainingAmount: number;
  /** Pourcentage de progression vers le seuil (0-100, plafonné à 100) */
  progress: number;
}

/**
 * Résultat du calcul des réductions
 */
export interface DiscountResult {
  /** Pourcentage total de réduction (0 pour Pack, progressif pour "À la carte") */
  totalDiscountPercent: number;
  /** Montant de la réduction en € */
  discountAmount: number;
  /** Liste des réductions détaillées */
  discounts: Array<{ name: string; percent: number; amount: number }>;
}

/**
 * État de l'option design-moe pour un mode
 */
export interface DesignMoeState {
  /** true si design-moe est activé */
  enabled: boolean;
  /** true si l'état est forcé (non modifiable par l'utilisateur) */
  forced: boolean;
  /** true si design-moe est inclus dans le prix (modes Pack) */
  includedInPrice: boolean;
}

// ============================================================================
// CONSTANTES
// ============================================================================

/**
 * Constantes métier pour le Pack Ailix
 * 
 * IMPORTANT : Ces valeurs sont définitives et ne doivent pas être modifiées
 * sans validation métier.
 */
export const PACK_AILIX_CONSTANTS = {
  /** Seuil de déblocage en jours */
  UNLOCK_THRESHOLD_DAYS: 220,
  /** Seuil de déblocage en euros (220 × 700€) */
  UNLOCK_THRESHOLD_AMOUNT: 154000,
  /** TJM mode "À la carte" */
  TJM_A_LA_CARTE: 700,
  /** TJM équivalent mode "Pack Ailix 1 an" (développeurs uniquement) */
  TJM_PACK_1AN: 588,
  /** TJM équivalent mode "Pack Ailix 2+ ans" (développeurs uniquement) */
  TJM_PACK_2ANS: 532,
  /** Pourcentage de réduction "À la carte" (legacy - non utilisé dans la logique) */
  ARKOLIA_DISCOUNT_PERCENT: 3,
  /** Seuil de déclenchement de la réduction "À la carte" (legacy - non utilisé dans la logique) */
  ARKOLIA_DISCOUNT_THRESHOLD: 100000,
  
  // ============================================================================
  // NOUVELLES CONSTANTES - Coûts fixes mensuels Pack Ailix
  // ============================================================================
  
  /** Jours ouvrés par an (base moyenne capacité développeur : 18,33j/mois × 12) */
  DAYS_PER_YEAR: 219.96,
  /** Jours ouvrés par mois par développeur (moyenne observée) */
  DAYS_PER_MONTH_PER_DEV: 18.33,
  /** Coût fixe mensuel Pack Ailix 1 an (MOE + Expert IA = 10,5j/mois × ~760€) */
  PACK_MONTHLY_COST_1AN: 7980,
  /** Coût fixe mensuel Pack Ailix 2+ ans (MOE + Expert IA = 10,5j/mois × ~762€) */
  PACK_MONTHLY_COST_2ANS: 8008,
  /** Jours inclus dans le Pack Ailix par mois (MOE 10j + Expert 0,5j) */
  PACK_DAYS_PER_MONTH: 10.5,

  // ============================================================================
  // FALLBACKS TJM "À la carte" pour le mode hybride
  // ============================================================================

  /** TJM par défaut pour Expert IA en "À la carte" hybride (si tjm_a_la_carte absent) */
  TJM_EXPERT_IA_A_LA_CARTE_DEFAULT: 4000,
  /** TJM par défaut pour MOE/Pilotage en "À la carte" hybride (si tjm_a_la_carte et clientTjmMoe absents) */
  TJM_MOE_A_LA_CARTE_DEFAULT: 1000,
} as const;

// ============================================================================
// CONFIG PAR DÉFAUT (reproduit les coûts hardcodés actuels avec 2 devs)
// ============================================================================

/**
 * Configuration par défaut du Pack Ailix.
 * Calibrée pour reproduire exactement les coûts actuels :
 * - Expert IA: 0.5j × 1000€ = 500€/mois (fixe)
 * - MOE: 3j × 2devs × 748€ = 4488€/mois
 * - Pilotage: 2j × 2devs × 748€ = 2992€/mois
 * - Total avec 2 devs: 7980€/mois = PACK_MONTHLY_COST_1AN
 */
export const DEFAULT_PACK_AILIX_CONFIG: PackAilixConfig = {
  tjm_pack_1an: 588,
  tjm_pack_2ans: 532,
  prestations: {
    expert_ia: {
      label: "Expert IA",
      jours_par_mois: 0.5,
      tjm_1an: 1000,
      tjm_2ans: 1000,
      per_dev: false,
      enabled_by_default: false,
    },
    moe: {
      label: "Maîtrise d'œuvre / Maquettes",
      jours_par_mois: 3,
      tjm_1an: 748,
      tjm_2ans: 750.8,
      per_dev: true,
      enabled_by_default: true,
    },
    pilotage: {
      label: "Pilotage projet",
      jours_par_mois: 2,
      tjm_1an: 748,
      tjm_2ans: 750.8,
      per_dev: true,
      enabled_by_default: true,
    },
  },
};

/**
 * Résout la config Pack Ailix : retourne DEFAULT si null/undefined, sinon la config fournie.
 * Garantit que les champs optionnels ont des valeurs de fallback cohérentes.
 */
export function resolvePackConfig(config?: PackAilixConfig | null): PackAilixConfig {
  if (!config) return DEFAULT_PACK_AILIX_CONFIG;
  return config;
}

/**
 * Retourne le modèle de comparaison actif pour un client.
 * Absent ou invalide = "legacy" (rétrocompatibilité totale).
 */
export function getComparisonModel(config?: PackAilixConfig | null): ComparisonModel {
  const model = config?.comparison_model;
  if (model === 'hybrid') return 'hybrid';
  return 'legacy';
}

/**
 * Détermine si le mode hybride est effectivement actif pour le scope courant.
 * Retourne true uniquement si :
 * 1. La config client définit comparison_model = 'hybrid'
 * 2. Le nombre de jours atteint le seuil d'éligibilité pack (>= 220j)
 */
export function isHybridActiveForScope(
  config?: PackAilixConfig | null,
  totalDays?: number,
): boolean {
  if (getComparisonModel(config) !== 'hybrid') return false;
  if (totalDays == null || totalDays <= 0) return false;
  return calculatePackAilixEligibility(totalDays).eligible;
}

/**
 * Calcule le coût mensuel des prestations (MOE, Expert IA, Pilotage) selon la config.
 *
 * @param mode - Mode de tarification (a_la_carte retourne 0)
 * @param nbDevs - Nombre de développeurs
 * @param config - Config Pack Ailix (null = defaults)
 * @param enabledPrestations - Map des prestations activées/désactivées (clé = id prestation)
 * @returns Coût mensuel des prestations en euros
 */
export function calculateMonthlyPrestationsCost(
  mode: PricingMode,
  nbDevs: number,
  config?: PackAilixConfig | null,
  enabledPrestations?: Record<string, boolean>,
): number {
  if (mode === 'a_la_carte') return 0;

  const resolved = resolvePackConfig(config);
  let total = 0;

  for (const [key, presta] of Object.entries(resolved.prestations)) {
    // Admin = plafond : l'utilisateur peut désactiver mais pas activer une prestation interdite par l'admin
    if (!presta.enabled_by_default) continue;
    const isEnabled = enabledPrestations && key in enabledPrestations
      ? enabledPrestations[key]
      : true;

    if (!isEnabled) continue;

    const tjm = mode === 'pack_ailix_1an' ? presta.tjm_1an : presta.tjm_2ans;
    const jours = presta.per_dev ? presta.jours_par_mois * nbDevs : presta.jours_par_mois;
    total += jours * tjm;
  }

  return total;
}

// ============================================================================
// FONCTIONS (STUBS - À IMPLÉMENTER AU PROMPT 3)
// ============================================================================

/**
 * Calcule l'éligibilité au Pack Ailix en fonction du nombre de jours de missions
 * 
 * @param totalDays - Nombre total de jours des missions sélectionnées
 * @returns Objet contenant l'éligibilité, les jours/montant restants et la progression
 * 
 * @example
 * const result = calculatePackAilixEligibility(219);
 * // { eligible: false, remaining: 1, remainingAmount: 700, progress: 99.55 }
 */
export function calculatePackAilixEligibility(totalDays: number): PackAilixEligibility {
  const { UNLOCK_THRESHOLD_DAYS, TJM_A_LA_CARTE } = PACK_AILIX_CONSTANTS;
  
  // Normaliser les jours négatifs à 0
  const safeDays = Math.max(0, totalDays);
  
  const eligible = safeDays >= UNLOCK_THRESHOLD_DAYS;
  const remaining = Math.max(0, UNLOCK_THRESHOLD_DAYS - safeDays);
  const remainingAmount = remaining * TJM_A_LA_CARTE;
  const progress = Math.min(100, (safeDays / UNLOCK_THRESHOLD_DAYS) * 100);
  
  return {
    eligible,
    remaining,
    remainingAmount,
    progress,
  };
}

/**
 * Retourne le TJM (Taux Journalier Moyen) pour un mode de tarification donné
 * 
 * @param mode - Mode de tarification
 * @returns TJM en euros
 * 
 * @example
 * getTjmForMode('a_la_carte'); // 700
 * getTjmForMode('pack_ailix_1an'); // 588
 */
export function getTjmForMode(
  mode: PricingMode,
  config?: PackAilixConfig | null,
  clientTjm?: number,
): number {
  const { TJM_A_LA_CARTE, TJM_PACK_1AN, TJM_PACK_2ANS } = PACK_AILIX_CONSTANTS;

  switch (mode) {
    case 'a_la_carte':
      return clientTjm ?? TJM_A_LA_CARTE;
    case 'pack_ailix_1an':
      return config ? resolvePackConfig(config).tjm_pack_1an : TJM_PACK_1AN;
    case 'pack_ailix_2ans':
      return config ? resolvePackConfig(config).tjm_pack_2ans : TJM_PACK_2ANS;
    default:
      // Fallback sécurisé vers "à la carte" pour éviter NaN
      console.warn(`[getTjmForMode] Mode inconnu: "${mode}", fallback vers TJM_A_LA_CARTE`);
      return clientTjm ?? TJM_A_LA_CARTE;
  }
}

/**
 * Calcule les réductions applicables pour un mode et un montant donnés
 * 
 * Règles :
 * - "À la carte" : remises progressives selon les paliers activés
 * - "Pack Ailix" : aucune réduction (non soumis aux remises volume)
 * 
 * @param mode - Mode de tarification
 * @param subtotal - Montant brut en euros
 * @param disabledVolumeDiscounts - Paliers désactivés (optionnel)
 * @returns Objet contenant le pourcentage, le montant et le détail des réductions
 * 
 * @example
 * getDiscountsForMode('a_la_carte', 150000);
 * // { totalDiscountPercent: 5, discountAmount: 7500, discounts: [...] }
 */
export function getDiscountsForMode(
  mode: PricingMode,
  subtotal: number,
  disabledVolumeDiscounts?: { "50k"?: boolean; "100k"?: boolean; "250k"?: boolean; "350k"?: boolean; "550k"?: boolean }
): DiscountResult {
  // Pour les modes Pack : aucune réduction (non soumis aux remises volume)
  if (mode === 'pack_ailix_1an' || mode === 'pack_ailix_2ans') {
    return {
      totalDiscountPercent: 0,
      discountAmount: 0,
      discounts: [],
    };
  }
  
  // Mode "À la carte" : remises progressives selon la config admin
  const totalDiscountPercent = calculateTotalDiscountPercentage(subtotal, disabledVolumeDiscounts);
  if (mode === 'a_la_carte' && totalDiscountPercent > 0) {
    const discountAmount = subtotal * (totalDiscountPercent / 100);
    return {
      totalDiscountPercent,
      discountAmount,
      discounts: [{
        name: `Réduction volume (-${totalDiscountPercent}%)`,
        percent: totalDiscountPercent,
        amount: discountAmount,
      }],
    };
  }
  
  // Par défaut : pas de réduction
  return {
    totalDiscountPercent: 0,
    discountAmount: 0,
    discounts: [],
  };
}

/**
 * Retourne l'état de l'option design-moe pour un mode de tarification
 * 
 * Règles :
 * - "À la carte" : design-moe désactivé et forcé
 * - "Pack Ailix" : design-moe activé, forcé et inclus dans le prix
 * 
 * @param mode - Mode de tarification
 * @returns État de design-moe (enabled, forced, includedInPrice)
 * 
 * @example
 * getDesignMoeStateForMode('pack_ailix_1an');
 * // { enabled: true, forced: true, includedInPrice: true }
 */
export function getDesignMoeStateForMode(mode: PricingMode): DesignMoeState {
  // Mode "À la carte" : design-moe désactivé et forcé
  if (mode === 'a_la_carte') {
    return {
      enabled: false,
      forced: true,
      includedInPrice: false,
    };
  }
  
  // Modes Pack : design-moe activé, forcé et inclus dans le prix
  return {
    enabled: true,
    forced: true,
    includedInPrice: true,
  };
}

/**
 * Calcule la durée du projet en mois
 * 
 * Formule : totalDays / (nbDevs × 18j/mois)
 * 
 * @param totalDays - Nombre total de jours de missions
 * @param nbDevs - Nombre de développeurs
 * @returns Durée en mois (arrondie à 1 décimale)
 * 
 * @example
 * calculateProjectDurationMonths(400, 2); // 10 mois
 */
export function calculateProjectDurationMonths(totalDays: number, nbDevs: number): number {
  const { DAYS_PER_MONTH_PER_DEV } = PACK_AILIX_CONSTANTS;
  
  if (nbDevs <= 0 || totalDays <= 0) return 0;
  
  const durationMonths = totalDays / (nbDevs * DAYS_PER_MONTH_PER_DEV);
  return Math.round(durationMonths * 10) / 10;
}

/**
 * Calcule la mensualité Pack Ailix selon le mode de tarification
 * 
 * Formules :
 * - À la carte : nbDevs × 18j × 700€
 * - Pack 1 an : (nbDevs × 18j × 588€) + 7 980€
 * - Pack 2+ ans : (nbDevs × 18j × 532€) + 8 008€
 * 
 * @param nbDevs - Nombre de développeurs
 * @param mode - Mode de tarification
 * @returns Mensualité en euros
 * 
 * @example
 * calculatePackAilixMonthly(2, 'pack_ailix_1an');
 * // (2 × 18 × 588) + 7980 = 29 148€/mois
 */
export function calculatePackAilixMonthly(
  nbDevs: number,
  mode: PricingMode,
  config?: PackAilixConfig | null,
  clientTjm?: number,
  enabledPrestations?: Record<string, boolean>,
): number {
  const {
    DAYS_PER_MONTH_PER_DEV,
    TJM_A_LA_CARTE,
    TJM_PACK_1AN,
    TJM_PACK_2ANS,
    PACK_MONTHLY_COST_1AN,
    PACK_MONTHLY_COST_2ANS,
  } = PACK_AILIX_CONSTANTS;

  if (nbDevs <= 0) return 0;

  const devDaysPerMonth = nbDevs * DAYS_PER_MONTH_PER_DEV;

  // Si config fourni → calcul dynamique des prestations
  if (config) {
    const tjm = getTjmForMode(mode, config, clientTjm);
    const devCost = devDaysPerMonth * tjm;
    const prestaCost = calculateMonthlyPrestationsCost(mode, nbDevs, config, enabledPrestations);
    return devCost + prestaCost;
  }

  // Chemin legacy (hardcodé)
  switch (mode) {
    case 'a_la_carte':
      return devDaysPerMonth * (clientTjm ?? TJM_A_LA_CARTE);

    case 'pack_ailix_1an':
      return (devDaysPerMonth * TJM_PACK_1AN) + PACK_MONTHLY_COST_1AN;

    case 'pack_ailix_2ans':
      return (devDaysPerMonth * TJM_PACK_2ANS) + PACK_MONTHLY_COST_2ANS;

    default:
      console.warn(`[calculatePackAilixMonthly] Mode inconnu: "${mode}", fallback vers a_la_carte`);
      return devDaysPerMonth * (clientTjm ?? TJM_A_LA_CARTE);
  }
}

/**
 * Calcule le coût total du projet selon le mode de tarification
 * 
 * Formules :
 * - À la carte : totalDays × 700€ - remises volume progressives
 * - Pack 1 an : (totalDays × 588€) + (nbMois × prestations mensuelles)
 * - Pack 2+ ans : (totalDays × 532€) + (nbMois × prestations mensuelles)
 * 
 * @param totalDays - Nombre total de jours de missions
 * @param nbDevs - Nombre de développeurs (nécessaire pour calculer nbMois)
 * @param mode - Mode de tarification
 * @param disabledVolumeDiscounts - Paliers désactivés (optionnel)
 * @returns Coût total en euros
 * 
 * @example
 * calculateTotalCostForMode(360, 2, 'pack_ailix_1an');
 * // (360 × 588) + (10 × 7980) = 211 680 + 79 800 = 291 480€
 */
export function calculateTotalCostForMode(
  totalDays: number,
  nbDevs: number,
  mode: PricingMode,
  disabledVolumeDiscounts?: { "50k"?: boolean; "100k"?: boolean; "250k"?: boolean; "350k"?: boolean; "550k"?: boolean },
  config?: PackAilixConfig | null,
  clientTjm?: number,
  enabledPrestations?: Record<string, boolean>,
): number {
  const {
    TJM_A_LA_CARTE,
    TJM_PACK_1AN,
    TJM_PACK_2ANS,
    PACK_MONTHLY_COST_1AN,
    PACK_MONTHLY_COST_2ANS,
  } = PACK_AILIX_CONSTANTS;

  if (totalDays <= 0 || nbDevs <= 0) return 0;

  const durationMonths = calculateProjectDurationMonths(totalDays, nbDevs);

  // Si config fourni → calcul dynamique des prestations
  if (config) {
    const tjm = getTjmForMode(mode, config, clientTjm);

    if (mode === 'a_la_carte') {
      const subtotal = totalDays * tjm;
      const totalDiscountPercent = calculateTotalDiscountPercentage(subtotal, disabledVolumeDiscounts);
      return totalDiscountPercent > 0 ? subtotal * (1 - totalDiscountPercent / 100) : subtotal;
    }

    const devCost = totalDays * tjm;
    const prestaMonthlyCost = calculateMonthlyPrestationsCost(mode, nbDevs, config, enabledPrestations);
    return devCost + (durationMonths * prestaMonthlyCost);
  }

  // Chemin legacy (hardcodé)
  switch (mode) {
    case 'a_la_carte': {
      const tjm = clientTjm ?? TJM_A_LA_CARTE;
      const subtotal = totalDays * tjm;
      const totalDiscountPercent = calculateTotalDiscountPercentage(subtotal, disabledVolumeDiscounts);
      if (totalDiscountPercent > 0) {
        return subtotal * (1 - totalDiscountPercent / 100);
      }
      return subtotal;
    }

    case 'pack_ailix_1an': {
      const devCost = totalDays * TJM_PACK_1AN;
      const packCost = durationMonths * PACK_MONTHLY_COST_1AN;
      return devCost + packCost;
    }

    case 'pack_ailix_2ans': {
      const devCost = totalDays * TJM_PACK_2ANS;
      const packCost = durationMonths * PACK_MONTHLY_COST_2ANS;
      return devCost + packCost;
    }

    default: {
      console.warn(`[calculateTotalCostForMode] Mode inconnu: "${mode}", fallback vers a_la_carte`);
      const tjm = clientTjm ?? TJM_A_LA_CARTE;
      const subtotal = totalDays * tjm;
      const totalDiscountPercent = calculateTotalDiscountPercentage(subtotal, disabledVolumeDiscounts);
      if (totalDiscountPercent > 0) {
        return subtotal * (1 - totalDiscountPercent / 100);
      }
      return subtotal;
    }
  }
}

// ============================================================================
// MOTEUR HYBRIDE — Comparaison alignée (dev réel + prestations mensualisées)
// ============================================================================

/**
 * Calcule la durée du projet en mois, arrondie à 0,5 mois (même règle que le slider).
 * Utilisée comme durée unique pour TOUS les modes en comparaison hybride.
 *
 * @param totalDays - Nombre total de jours de missions
 * @param nbDevs - Nombre de développeurs
 * @returns Durée en mois (arrondie à 0,5 près, minimum 2 mois)
 */
export function calculateHybridDurationMonths(totalDays: number, nbDevs: number): number {
  const { DAYS_PER_MONTH_PER_DEV } = PACK_AILIX_CONSTANTS;
  if (nbDevs <= 0 || totalDays <= 0) return 2;

  const exactDuration = totalDays / (nbDevs * DAYS_PER_MONTH_PER_DEV);
  const roundedDuration = Math.round(exactDuration * 2) / 2;
  return Math.max(2, roundedDuration);
}

/**
 * Résout le TJM "À la carte" d'une prestation en mode hybride.
 * Ordre de priorité : presta.tjm_a_la_carte > fallback par type.
 *
 * @param prestaKey - Clé de la prestation (expert_ia, moe, pilotage)
 * @param presta - Configuration de la prestation
 * @param clientTjmMoe - TJM MOE du client (fallback pour moe/pilotage)
 */
function resolvePrestaALaCarteTjm(
  prestaKey: string,
  presta: PackPrestationConfig,
  clientTjmMoe?: number,
): number {
  if (presta.tjm_a_la_carte != null && presta.tjm_a_la_carte > 0) {
    return presta.tjm_a_la_carte;
  }
  // Fallback par type (constantes documentées dans PACK_AILIX_CONSTANTS)
  const { TJM_EXPERT_IA_A_LA_CARTE_DEFAULT, TJM_MOE_A_LA_CARTE_DEFAULT } = PACK_AILIX_CONSTANTS;
  if (prestaKey === 'expert_ia') return TJM_EXPERT_IA_A_LA_CARTE_DEFAULT;
  return clientTjmMoe ?? TJM_MOE_A_LA_CARTE_DEFAULT;
}

/**
 * Calcule le coût mensuel des prestations pour TOUS les modes (y compris "À la carte")
 * en mode de comparaison hybride.
 *
 * En legacy, "À la carte" retourne 0 (pas de prestations).
 * En hybride, "À la carte" inclut les mêmes prestations que les packs pour comparaison alignée.
 *
 * @param mode - Mode de tarification
 * @param nbDevs - Nombre de développeurs
 * @param config - Config Pack Ailix du client
 * @param clientTjmMoe - TJM MOE du client (fallback pour À la carte moe/pilotage)
 * @param enabledPrestations - Prestations activées/désactivées (toggles Finalisation)
 */
export function calculateHybridMonthlyPrestationsCost(
  mode: PricingMode,
  nbDevs: number,
  config?: PackAilixConfig | null,
  clientTjmMoe?: number,
  enabledPrestations?: Record<string, boolean>,
): number {
  const resolved = resolvePackConfig(config);
  let total = 0;

  for (const [key, presta] of Object.entries(resolved.prestations)) {
    // Admin = plafond : l'utilisateur peut désactiver mais pas activer une prestation interdite par l'admin
    if (!presta.enabled_by_default) continue;
    const isEnabled = enabledPrestations && key in enabledPrestations
      ? enabledPrestations[key]
      : true;

    if (!isEnabled) continue;

    // TJM prestations fixe (à la carte) : seul le TJM dev change entre les modes.
    const tjm = resolvePrestaALaCarteTjm(key, presta, clientTjmMoe);

    const jours = presta.per_dev ? presta.jours_par_mois * nbDevs : presta.jours_par_mois;
    total += jours * tjm;
  }

  return total;
}

/**
 * Ligne de remise détaillée pour l'affichage UI.
 */
export interface HybridDiscountLine {
  name: string;
  amount: number;
  type: 'volume' | 'exceptional' | 'group';
}

/**
 * Résultat détaillé du calcul hybride pour un mode.
 */
export interface HybridCostResult {
  /** Coût développement (totalDays × TJM dev) */
  devCost: number;
  /** Coût mensuel des prestations */
  monthlyPrestaCost: number;
  /** Coût total des prestations sur le projet (mensuel × durée) */
  totalPrestaCost: number;
  /** Durée unifiée en mois (arrondi 0,5) */
  durationMonths: number;
  /** Sous-total avant remises (dev + prestations) */
  subtotal: number;
  /** Montant total des remises (groupe + volume + exceptionnelle) */
  discountAmount: number;
  /** Pourcentage de remise volume */
  discountPercent: number;
  /** Montant de la remise exceptionnelle (0 si pas de remise exceptionnelle) */
  exceptionalDiscountAmount: number;
  /** Lignes de remises détaillées pour l'affichage UI */
  discounts: HybridDiscountLine[];
  /** Total net (subtotal - remises) */
  total: number;
}

/**
 * Calcule le coût total comparable pour un mode en comparaison hybride.
 * Source unique de calcul pour Navigation, Finalisation et Modalité de paiement.
 *
 * Formule hybride :
 * - devCost = totalDays × TJM_dev(mode)
 * - durationMonths = arrondi 0,5 de totalDays / (nbDevs × 18)
 * - prestaCost = durationMonths × Σ(jours_par_mois × multiplicateur × TJM_presta(mode))
 * - subtotal = devCost + prestaCost + options complémentaires non-core
 * - total :
 *   - À la carte : subtotal × (1 - remises volume)
 *   - Pack : subtotal (pas de remises volume)
 *
 * @param totalDays - Jours de missions réels
 * @param nbDevs - Nombre de développeurs
 * @param mode - Mode de tarification
 * @param config - Config Pack Ailix du client
 * @param clientTjm - TJM dev du client (pour À la carte)
 * @param clientTjmMoe - TJM MOE du client (fallback pour prestations À la carte)
 * @param enabledPrestations - Toggles prestations
 * @param disabledVolumeDiscounts - Paliers de remise désactivés
 * @param complementaryOptionsCost - Coût des options complémentaires non-core (déjà calculé en amont)
 * @param exceptionalDiscountPercentage - Pourcentage de remise exceptionnelle (optionnel, appliqué sur le subtotal en À la carte)
 * @param groupDiscountAmount - Montant pré-calculé de la remise groupe (optionnel, réduit l'assiette avant remises volume/exceptionnelle)
 */
export function calculateHybridTotalCostForMode(
  totalDays: number,
  nbDevs: number,
  mode: PricingMode,
  config?: PackAilixConfig | null,
  clientTjm?: number,
  clientTjmMoe?: number,
  enabledPrestations?: Record<string, boolean>,
  disabledVolumeDiscounts?: { "50k"?: boolean; "100k"?: boolean; "250k"?: boolean; "350k"?: boolean; "550k"?: boolean },
  complementaryOptionsCost?: number,
  exceptionalDiscountPercentage?: number,
  groupDiscountAmount?: number,
): HybridCostResult {
  if (totalDays <= 0 || nbDevs <= 0) {
    return { devCost: 0, monthlyPrestaCost: 0, totalPrestaCost: 0, durationMonths: 2, subtotal: 0, discountAmount: 0, discountPercent: 0, exceptionalDiscountAmount: 0, discounts: [], total: 0 };
  }

  const tjmDev = getTjmForMode(mode, config, clientTjm);
  const durationMonths = calculateHybridDurationMonths(totalDays, nbDevs);
  const devCost = totalDays * tjmDev;
  const monthlyPrestaCost = calculateHybridMonthlyPrestationsCost(mode, nbDevs, config, clientTjmMoe, enabledPrestations);
  const totalPrestaCost = durationMonths * monthlyPrestaCost;
  const optionsCost = complementaryOptionsCost ?? 0;
  const subtotal = devCost + totalPrestaCost + optionsCost;

  // Remise groupe (réduit l'assiette avant les autres remises)
  const groupAmt = groupDiscountAmount ?? 0;
  const adjustedSubtotal = Math.max(0, subtotal - groupAmt);
  let discountPercent = 0;
  let volumeDiscountAmount = 0;
  let exceptionalDiscountAmount = 0;
  const discountLines: HybridDiscountLine[] = [];

  if (groupAmt > 0) {
    discountLines.push({
      name: 'Remise package groupe',
      amount: groupAmt,
      type: 'group',
    });
  }

  if (mode === 'a_la_carte') {
    // 1. Remise volume (sur le subtotal ajusté après remise groupe)
    discountPercent = calculateTotalDiscountPercentage(adjustedSubtotal, disabledVolumeDiscounts);
    volumeDiscountAmount = discountPercent > 0 ? adjustedSubtotal * (discountPercent / 100) : 0;
    if (volumeDiscountAmount > 0) {
      discountLines.push({
        name: `Réduction volume (-${discountPercent}%)`,
        amount: volumeDiscountAmount,
        type: 'volume',
      });
    }

    // 2. Remise exceptionnelle (sur le subtotal ajusté, même assiette que useProjectPricing)
    if (exceptionalDiscountPercentage && exceptionalDiscountPercentage > 0) {
      exceptionalDiscountAmount = adjustedSubtotal * (exceptionalDiscountPercentage / 100);
      discountLines.push({
        name: `Remise exceptionnelle (-${exceptionalDiscountPercentage}%)`,
        amount: exceptionalDiscountAmount,
        type: 'exceptional',
      });
    }
  }

  const totalDiscountAmount = groupAmt + volumeDiscountAmount + exceptionalDiscountAmount;
  const total = subtotal - totalDiscountAmount;

  return {
    devCost,
    monthlyPrestaCost,
    totalPrestaCost,
    durationMonths,
    subtotal,
    discountAmount: totalDiscountAmount,
    discountPercent,
    exceptionalDiscountAmount,
    discounts: discountLines,
    total,
  };
}

/**
 * Calcule le détail des jours projet par prestation en mode hybride.
 * Utile pour la transparence UI et le debug.
 *
 * @returns Record<prestationKey, { label, joursParMois, joursTotalProjet, tjm, coutTotal }>
 */
export function calculateHybridPrestationsBreakdown(
  totalDays: number,
  nbDevs: number,
  mode: PricingMode,
  config?: PackAilixConfig | null,
  clientTjmMoe?: number,
  enabledPrestations?: Record<string, boolean>,
): Record<string, { label: string; joursParMois: number; joursTotalProjet: number; tjm: number; coutTotal: number }> {
  const resolved = resolvePackConfig(config);
  const durationMonths = calculateHybridDurationMonths(totalDays, nbDevs);
  const result: Record<string, { label: string; joursParMois: number; joursTotalProjet: number; tjm: number; coutTotal: number }> = {};

  for (const [key, presta] of Object.entries(resolved.prestations)) {
    // Admin = plafond : l'utilisateur peut désactiver mais pas activer une prestation interdite par l'admin
    if (!presta.enabled_by_default) continue;
    const isEnabled = enabledPrestations && key in enabledPrestations
      ? enabledPrestations[key]
      : true;

    if (!isEnabled) continue;

    // TJM prestations fixe (à la carte) : seul le TJM dev change entre les modes.
    const tjm = resolvePrestaALaCarteTjm(key, presta, clientTjmMoe);

    const joursParMois = presta.per_dev ? presta.jours_par_mois * nbDevs : presta.jours_par_mois;
    const joursTotalProjet = Math.round(joursParMois * durationMonths * 10) / 10;

    result[key] = {
      label: presta.label,
      joursParMois,
      joursTotalProjet,
      tjm,
      coutTotal: Math.round(joursTotalProjet * tjm * 100) / 100,
    };
  }

  return result;
}

/**
 * Calcule le prix total pour un ensemble de missions selon le mode de tarification
 *
 * Note : Cette fonction est conservée pour rétrocompatibilité. Utiliser calculateTotalCostForMode
 * pour les nouveaux développements qui nécessitent le coût avec les frais fixes mensuels.
 * 
 * Formule simplifiée : totalDays × TJM - réductions
 * 
 * @param missions - Liste des missions sélectionnées
 * @param mode - Mode de tarification
 * @param disabledVolumeDiscounts - Paliers désactivés (optionnel)
 * @returns Prix total en euros (sans les coûts fixes mensuels Pack Ailix)
 * 
 * @example
 * const missions = [{ duration_days: 100, ... }, { duration_days: 50, ... }];
 * calculateTotalForMode(missions, 'pack_ailix_1an');
 * // 150 × 588 = 88 200€
 */
export function calculateTotalForMode(
  missions: Mission[],
  mode: PricingMode,
  disabledVolumeDiscounts?: { "50k"?: boolean; "100k"?: boolean; "250k"?: boolean; "350k"?: boolean; "550k"?: boolean }
): number {
  // Calculer le total des jours
  const totalDays = missions.reduce((sum, mission) => sum + (mission.duration_days || 0), 0);
  
  // Calculer le sous-total brut
  const tjm = getTjmForMode(mode);
  const subtotal = totalDays * tjm;
  
  // Appliquer les réductions
  const { discountAmount } = getDiscountsForMode(mode, subtotal, disabledVolumeDiscounts);
  
  return subtotal - discountAmount;
}
